import React, { useEffect, useState, useCallback } from 'react';
import { Grid, Tabs, Tab } from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import * as actions from '../../../../store/index';
import DicomFilters from './DicomFilters/DicomFilters';
import { fetchGet, fetchPut } from '../../../../helpers/fetching';
import DraggableDialog from '../DraggableDialog/DraggableDialog';
import DiagnosticsModule from './DiagnosticsModule/DiagnosticsModule';
import { setSnackbar, fetchGlobalSettings, setReportConfiguration } from '../../../../store';
import Interfaces from './Interfaces/Interfaces';
import InfoModule from './InfoModule/InfoModule';

const useStyles = makeStyles((theme) => ({
  Dialog: {
    '& .MuiDialog-paper': {
      minHeight: '65vh',
    },
  },
  tabs: {
    borderRight: `1px solid ${theme.palette.divider}`,
    letterSpacing: '0.1rem',
    textTransform: 'uppercase',
    color: theme.palette.secondary.main,
    alignItems: 'center',
  },
}));

const DEFAULT_LESIONS_TO_SHOW = {
  calc_vessel: false,
  calc_birads2: false,
  calc_birads3: true,
  calc_birads4: true,
  calc_birads5: true,
  opac_birads2: false,
  opac_birads3: true,
  opac_birads4: true,
  opac_birads5: true,
  opac_lesionKnown: false,
};

const initialReportConfiguration = {
  language: 'en',
  file_format: 'jpeg',
  sending_delay: 60,
  contour_shown: true,
  projections_big: false,
  hide_operator_name: false,
  lesions_to_show: { ...DEFAULT_LESIONS_TO_SHOW },
  image_compression: false,
  show_missed_findings: false,
  crop_to_breast: false,
  routing_tag_kind: '',
  routing_tag: '',
  routing_tag_group: '',
  routing_tag_creator: '',
  routing_tag_offset: '',
};

const initialSettings = {
  filter_dcm: false,
  anonymize: false,
  store_dicoms: false,
  discFree: 0,
  discSize: 0,
  dicomCount: 0,
  presentation_lesions: '',
  presentation_quality: '',
  inverted_view_position: false,
  crop_to_breast: false,
  remove_after_days: null,
  priors_report: false,
  priors_number_limit: 5,
  priors_years_limit: 5,
  report_configuration: { ...initialReportConfiguration },
};

function stripPresentationBoxes(value) {
  const normalized = value || 'boxes';
  return normalized.endsWith('_boxes') ? normalized.replace('_boxes', '') : normalized;
}

function mergeReportConfig(source) {
  return {
    ...initialReportConfiguration,
    ...source,
    lesions_to_show: {
      ...DEFAULT_LESIONS_TO_SHOW,
      ...(source?.lesions_to_show || {}),
    },
  };
}

function buildFormState(nodeData, globalSettings) {
  const form = {
    ...initialSettings,
    ...nodeData,
    presentation_quality: nodeData.presentation_parenchyma || initialSettings.presentation_quality,
    presentation_lesions: stripPresentationBoxes(nodeData.presentation_lesions),
  };

  if (globalSettings?.isReady) {
    const { user_settings, report_configuration } = globalSettings;
    return {
      ...form,
      presentation_lesions: stripPresentationBoxes(
        user_settings.presentation_lesions ?? form.presentation_lesions,
      ),
      presentation_quality: user_settings.presentation_quality ?? form.presentation_quality,
      inverted_view_position: user_settings.inverted_view_position ?? form.inverted_view_position,
      crop_to_breast: user_settings.crop_to_breast ?? form.crop_to_breast,
      priors_report: user_settings.priors_report ?? form.priors_report,
      priors_number_limit: user_settings.priors_number_limit ?? form.priors_number_limit,
      priors_years_limit: user_settings.priors_years_limit ?? form.priors_years_limit,
      report_configuration: mergeReportConfig(report_configuration),
    };
  }

  return form;
}

function toUserSettingsPutBody(settings) {
  const {
    report_configuration: _report,
    discFree: _discFree,
    discSize: _discSize,
    dicomCount: _dicomCount,
    presentation_quality,
    ...userFields
  } = settings;

  return {
    filter_dcm: userFields.filter_dcm,
    anonymize: userFields.anonymize,
    store_dicoms: userFields.store_dicoms,
    presentation_lesions: userFields.presentation_lesions,
    presentation_microcalc: userFields.presentation_microcalc,
    presentation_parenchyma: presentation_quality || 'contours',
    presentation_folds: userFields.presentation_folds,
    presentation_blur: userFields.presentation_blur,
    presentation_pectoralis: userFields.presentation_pectoralis,
    remove_after_days: userFields.remove_after_days,
    inverted_view_position: userFields.inverted_view_position,
    crop_to_breast: !!userFields.crop_to_breast,
    priors_number_limit: userFields.priors_number_limit,
    priors_years_limit: userFields.priors_years_limit,
    priors_report: userFields.priors_report,
  };
}

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role='tabpanel'
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}>
      {children}
    </div>
  );
}

function Configuration() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const theme = useTheme();
  const currentRole = localStorage.getItem('role');
  const globalSettings = useSelector((state) => state.global_settings);
  const globalReady = useSelector((state) => state.global_settings.isReady);
  const currentExam = useSelector((state) => state.examinations.currentExam);
  const activeDialog = useSelector((state) => state.gui.activeDialog);

  const [dicomFilters, setDicomFilters] = useState([]);
  const [settings, setSettings] = useState(initialSettings);
  const [settingsChanged, setSettingsChanged] = useState(false);
  const [filtersChanged, setFiltersChanged] = useState(false);
  const [value, setValue] = useState(0);
  const [reportRulesChanged, setReportRulesChanged] = useState(false);

  const managementAccess = ['admin', 'supervisor', 'radiologist', 'service'].includes(currentRole);
  const serviceAccess = ['admin', 'service'].includes(currentRole);
  const priors_fetching = globalSettings?.premium_features?.priors_fetching;
  const query_worklist = globalSettings?.premium_features?.query_worklist;

  useEffect(() => {
    if (!managementAccess) setValue(2);
    else if (managementAccess && !serviceAccess) setValue(1);
    else setValue(0);
  }, [managementAccess, serviceAccess]);

  const markSettingsDirty = useCallback(() => setSettingsChanged(true), []);

  const fetchFilters = useCallback(async () => {
    const response = await fetchGet('filters');
    if (response.success) {
      setDicomFilters(response.data);
      return;
    }
    dispatch(setSnackbar({ msg: `api_messages.${response.msg}`, severity: 'error' }));
  }, [dispatch]);

  const fetchSettings = useCallback(async () => {
    if (globalSettings?.isReady) {
      const settingsRes = await fetchGet('settings');
      const nodeData = settingsRes.success ? settingsRes.data : {};
      setSettings(buildFormState(nodeData, globalSettings));
      return;
    }

    const [settingsRes, reportRes] = await Promise.all([
      fetchGet('settings'),
      fetchGet('report_configuration'),
    ]);
    const nodeData = settingsRes.success ? settingsRes.data : {};
    const reportData = reportRes.success ? reportRes.data : {};
    setSettings({
      ...buildFormState(nodeData, globalSettings),
      report_configuration: mergeReportConfig(reportData),
    });
  }, [globalSettings]);

  useEffect(() => {
    if (activeDialog !== 'config') return;
    fetchFilters();
    fetchSettings();
  }, [activeDialog, globalReady, fetchFilters, fetchSettings]);

  const updateSettings = useCallback(
    (name, value) => {
      setSettings((prev) => ({ ...prev, [name]: value }));
      markSettingsDirty();
    },
    [markSettingsDirty],
  );

  const updateReportField = useCallback(
    (name, value) => {
      setSettings((prev) => ({
        ...prev,
        report_configuration: { ...prev.report_configuration, [name]: value },
      }));
      markSettingsDirty();
    },
    [markSettingsDirty],
  );

  function updateSettingsCheckbox(event) {
    const { checked, name } = event.target;
    updateSettings(name, checked ? 1 : 0);
  }

  function updateSettingsValue(event) {
    const { value: raw, name } = event.target;
    if (raw !== '' && raw != null) {
      const num = parseInt(raw, 10);
      if (!Number.isNaN(num) && Number.isInteger(num)) {
        updateSettings(name, num);
        return;
      }
    }
    updateSettings(name, raw);
  }

  function updateReportFieldValue(event) {
    const num = parseInt(event.target.value, 10);
    updateReportField(event.target.name, Number.isNaN(num) ? 0 : num);
  }

  function updateReportFieldCheckbox(event) {
    updateReportField(event.target.name, event.target.checked);
  }

  function updateReportConfig(event) {
    const { name } = event.target;
    setSettings((prev) => {
      const report = { ...prev.report_configuration };
      if (name in report.lesions_to_show) {
        report.lesions_to_show = {
          ...report.lesions_to_show,
          [name]: !report.lesions_to_show[name],
        };
      } else {
        report[name] = !report[name];
      }
      return { ...prev, report_configuration: report };
    });
    markSettingsDirty();
  }

  async function putSettings() {
    const loggedUser = localStorage.getItem('username');
    const [userRes, reportRes] = await Promise.all([
      fetchPut('settings', { ...toUserSettingsPutBody(settings), loggedUser }),
      fetchPut('report_configuration', { ...settings.report_configuration, loggedUser }),
    ]);

    if (userRes.success && reportRes.success) {
      dispatch(setReportConfiguration(settings.report_configuration));
      dispatch(actions.updateDiagnosticsOverlaysToShow());
      dispatch(setSnackbar({ msg: 'Settings.snack_settings_saved', severity: 'success' }));
      dispatch(fetchGlobalSettings());
    } else {
      dispatch(setSnackbar({ msg: 'Settings.error_saving', severity: 'error' }));
    }
  }

  async function putFilters() {
    const response = await fetchPut('filters', dicomFilters);
    dispatch(
      setSnackbar({
        msg: response.success ? 'Settings.snack_filters_saved' : 'Settings.cnack_filters_failed',
        severity: response.success ? 'success' : 'error',
      }),
    );
  }

  function saveChanges() {
    if (settingsChanged) putSettings();
    if (filtersChanged) putFilters();
    if (reportRulesChanged && currentExam) {
      dispatch(actions.fetchResults());
    }
    setSettingsChanged(false);
    setFiltersChanged(false);
    setReportRulesChanged(false);
    dispatch(actions.setActiveDialog(null));
  }

  const report = settings.report_configuration;

  return (
    <DraggableDialog
      className={classes.Dialog}
      maxWidth='lg'
      title={t('Menu.config_bbox')}
      open={activeDialog === 'config'}
      style={{ zIndex: theme.zIndex.main_drawer }}
      actions={[{ onClick: saveChanges, color: 'primary', label: t('Settings.save') }]}
      onClose={() => dispatch(actions.setActiveDialog(null))}>
      <Grid container direction='row' justifyContent='space-evenly' alignItems='flex-start' spacing={1}>
        <Grid item xs={2}>
          <Tabs
            className={classes.tabs}
            orientation='vertical'
            value={value}
            onChange={(_, tab) => setValue(tab)}>
            <Tab label={t('Settings.filters')} disabled={!serviceAccess} />
            <Tab label={t('Settings.interfaces')} disabled={!managementAccess} />
            <Tab label={t('Settings.diagnostics_config')} />
            <Tab label={t('Menu.info')} />
          </Tabs>
        </Grid>

        <Grid item xs={10}>
          {serviceAccess && (
            <TabPanel value={value} index={0}>
              <DicomFilters
                items={dicomFilters}
                addItem={(f) => {
                  setDicomFilters((prev) => [...prev, f]);
                  setFiltersChanged(true);
                }}
                deleteItem={(f) => {
                  setDicomFilters((prev) => prev.filter((x) => x.name !== f.name));
                  setFiltersChanged(true);
                }}
                checked={!!settings.filter_dcm}
                onChange={updateSettingsCheckbox}
                name='filter_dcm'
                setSnackbar={(obj) => dispatch(setSnackbar(obj))}
              />
            </TabPanel>
          )}

          {managementAccess && (
            <TabPanel value={value} index={1}>
              <Interfaces
                priors_fetching={priors_fetching}
                query_worklist={query_worklist}
                settings={settings}
                report={report}
                updateSettingsCheckbox={updateSettingsCheckbox}
                updateSettingsValue={updateSettingsValue}
                setReportRulesChanged={setReportRulesChanged}
                updateReportField={updateReportField}
                updateReportFieldValue={updateReportFieldValue}
                updateReportFieldCheckbox={updateReportFieldCheckbox}
              />
            </TabPanel>
          )}

          <TabPanel value={value} index={2}>
            <DiagnosticsModule
              report={report}
              updateReportConfig={updateReportConfig}
              updateReportField={updateReportField}
            />
          </TabPanel>

          <TabPanel value={value} index={3}>
            <InfoModule />
          </TabPanel>
        </Grid>
      </Grid>
    </DraggableDialog>
  );
}

export default Configuration;
